// eslint-disable-line

import { Cloudinary, Transformation as CloudinaryTransformation } from 'cloudinary-core';
import IImage from '@app/types/data/base/IImage';
import ICloudinaryAsset, { isCloudinaryAsset } from '@libraries/cloudinary/ICloudinaryAsset';
import { getModule as getStoreModule } from 'vuex-module-decorators';
import Axios from 'axios';
import IVideo from '../types/data/base/IVideo';
import ImageSrcset from '../types/data/base/IImageSrcset';
import AppStoreModule from '../store/modules/AppStoreModule';
import AppModule from '../types/base/AppModule';

const appStore: AppStoreModule = getStoreModule(AppStoreModule);

const cloudinaryInstance = new Cloudinary({
	cloud_name: process.env.CLOUDINARY_CLOUD_NAME, // eslint-disable-line
	cname: process.env.CLOUDINARY_CNAME,
	secure_distribution: process.env.CLOUDINARY_CNAME, // eslint-disable-line
	secure: true,
	private_cdn: true // eslint-disable-line
});

interface ITransformedCloudinaryImage extends IImage {
	fileInfoUrl?: string;
}

export default class CloudinaryTransform {
	private static getTransformationChainForAsset(
		asset: ICloudinaryAsset,
		transformation?: CloudinaryTransformation | CloudinaryTransformation.Options
	): CloudinaryTransformation {
		let transformationChain: CloudinaryTransformation = CloudinaryTransformation.new();

		// use transformation defined in Contentful asset first
		if (asset.derived && asset.derived.length > 0) {
			transformationChain = CloudinaryTransformation.new({
				rawTransformation: asset.derived[0].raw_transformation
			}).chain();
		}
		// apply custom transformation
		if (transformation) {
			transformationChain.transformation(
				transformation instanceof CloudinaryTransformation
					? (transformation.toOptions() as CloudinaryTransformation.Options[])
					: [transformation]
			);
		}

		return transformationChain;
	}

	/**
	 * Transforms Cloudinary widget asset data into IImage. Returned image dimensions will be the original's
	 * If transformations have been applied to the asset by the widget, these will be applied first
	 * Optional transformations in parameter `transformation`, these will be applied last
	 * @param asset
	 * @param transformation
	 */
	public static transformImageAsset(
		asset: ICloudinaryAsset,
		transformation?: CloudinaryTransformation | CloudinaryTransformation.Options
	): ITransformedCloudinaryImage {

		let appliedTransformation: CloudinaryTransformation | CloudinaryTransformation.Options | undefined = transformation;

		// enforce webp in present and select, use f_auto in share module
		if (appStore.activeAppModule === AppModule.Share) {
			if (appliedTransformation instanceof CloudinaryTransformation) {
				if (!appliedTransformation.toPlainObject || !(appliedTransformation.toPlainObject as any).fetchFormat) {
					appliedTransformation.fetchFormat('auto');
					appliedTransformation.quality('auto');
				}
			} else if (appliedTransformation && !appliedTransformation.fetchFormat) {
				appliedTransformation = { ...appliedTransformation };
				appliedTransformation.fetchFormat = appliedTransformation.fetchFormat || 'auto';
				appliedTransformation.quality = appliedTransformation.quality || 'auto';
			}
		} else if (appliedTransformation instanceof CloudinaryTransformation) {
			if (!appliedTransformation.toPlainObject || !(appliedTransformation.toPlainObject as any).format) {
				appliedTransformation.format('webp');
			}
		} else if (appliedTransformation && !appliedTransformation.format) {
			appliedTransformation = { ...appliedTransformation };
			appliedTransformation.format = appliedTransformation.format || 'webp';
		}

		const transformationChain: CloudinaryTransformation = this.getTransformationChainForAsset(
			asset,
			appliedTransformation
		);

		const image: ITransformedCloudinaryImage = {
			alt: '',
			width: asset.width,
			height: asset.height
		};

		let srcSetWidths: number[] | undefined;

		let transformedAssetWidth: number = asset.width;

		((transformationChain.toPlainObject() as any).transformation as any[]).forEach(
			(partialTransformation: any): void => {
				if (partialTransformation.raw_transformation) {
					const matches: string[] = partialTransformation.raw_transformation.match('(^|,)w_([0-9.]+)');
					if (matches.length > 2) {
						const transformWidth = parseFloat(matches[2]);
						transformedAssetWidth = transformWidth > 1 ? transformWidth : transformedAssetWidth * transformWidth;
					}
				}

				if (partialTransformation.width) {
					const transformWidth = parseFloat(partialTransformation.width);
					transformedAssetWidth = transformWidth > 1 ? transformWidth : transformedAssetWidth * transformWidth;
				}
			}
		);

		const minAssetWidth: number = 180;
		const maxAssetWidth: number = Math.min(5000, transformedAssetWidth);

		// srcsets must only be used in share mode
		if (transformedAssetWidth > minAssetWidth && appStore.activeAppModule === AppModule.Share) {
			srcSetWidths = [maxAssetWidth];

			// build srcset widths array by adding assets 1/2 * smallest size yet
			// until min asset size is reached
			while (srcSetWidths[0] > minAssetWidth) {
				srcSetWidths.unshift(Math.max(Math.round(srcSetWidths[0] / 2), minAssetWidth));
			}
		}

		if (!srcSetWidths || !srcSetWidths.length) {
			image.src = cloudinaryInstance.url(asset.public_id, {
				...transformationChain.toOptions(),
				use_root_path: true // eslint-disable-line
			});

			image.fileInfoUrl = cloudinaryInstance.url(asset.public_id, {
				...transformationChain.toOptions(),
				flags: 'getinfo',
				use_root_path: true // eslint-disable-line
			});
		} else {
			image.srcset = [];

			// ensure srcset items go from smallest to largest to have a comprehensible order
			const sortedSrcsetWidths: number[] = srcSetWidths.sort(
				(widthA: number, widthB: number): number => widthA - widthB
			);

			sortedSrcsetWidths.forEach((width: number): void => {
				const transformOptions: CloudinaryTransformation.Options = JSON.parse(JSON.stringify({... transformationChain.toOptions(), use_root_path: true})); // eslint-disable-line
				(transformOptions.transformation as CloudinaryTransformation.Options[]).push({ width, crop: 'limit' });

				const imageSrcSetItem: ImageSrcset = {
					width,
					src: cloudinaryInstance.url(asset.public_id, transformOptions)
				};

				(image.srcset as ImageSrcset[]).push(imageSrcSetItem);
			});
		}

		return image;
	}

	/**
	 * Transforms Cloudinary widget asset data into IVideo. Returned video dimensions will be the original's
	 * Transformations to the asset by the widget are ignored as on-the-fly transforms for videos are hardly possible in Cloudinary anyway
	 * Optional transformations in parameter `transformation`
	 * @param asset
	 * @param transformation
	 * @param posterImage? undefined = no poster image, 'auto' = frame 0 auto poster image, ICloudinaryAsset = custom  poster image
	 * @param posterTransformation?
	 */
	public static transformVideoAsset(
		asset: ICloudinaryAsset,
		transformation?: CloudinaryTransformation | CloudinaryTransformation.Options,
		posterImage?: 'auto' | ICloudinaryAsset,
		posterTransformation?: CloudinaryTransformation | CloudinaryTransformation.Options
	): IVideo {
		const transformationChain: CloudinaryTransformation = this.getTransformationChainForAsset(
			{ ...asset, derived: undefined },
			transformation
		);
		let posterTransformationChain: CloudinaryTransformation | undefined;
		let poster: string | undefined;

		if (isCloudinaryAsset(posterImage)) {
			posterTransformationChain = this.getTransformationChainForAsset(posterImage, posterTransformation);
			poster = cloudinaryInstance.url(posterImage.public_id, {
				...(posterTransformationChain || transformationChain).toOptions()
			});
		} else if (posterImage === 'auto') {
			poster = cloudinaryInstance.video_thumbnail_url(asset.public_id, {
				...(posterTransformationChain || transformationChain).toOptions(),
				startOffset: 0 // eslint-disable-line @typescript-eslint/naming-convention
			});
		}

		return {
			poster,
			sources: [
				/*
				Unused in present mode
				{
					type: 'video/webm',
					src: cloudinaryInstance.video_url(asset.public_id, {
						...(transformationChain ? transformationChain.toOptions() : {}),
						format: 'webm',
					})
				},*/
				{
					type: 'video/mp4',
					src: cloudinaryInstance.video_url(asset.public_id, {
						width: 1920,
						height: 1080,
						crop: 'limit',
						videoCodec: 'h264:baseline:3.1',
						audioCodec: 'aac',
						// fetchFormat: 'mp4',
						// transformation: 'hd-mp4',
						...(transformationChain ? transformationChain.toOptions() : {}),
						format: 'mp4'
					})
				} /*
				Unused in present mode
				{
					type: 'video/ogg',
					src: cloudinaryInstance.video_url(asset.public_id, {
						...(transformationChain ? transformationChain.toOptions() : {}),
						format: 'ogv',
					})
				}*/
			],
			width: asset.width,
			height: asset.height
		};
	}

	/**
	 * Returns the estimated output size of the image based on the image dimensions
	 * and transformation values. (So the new image dimensions after the transformation)
	 * @private
	 */
	public static async getOutputSize(
		image: ITransformedCloudinaryImage | IImage
	): Promise<{ width: number; height: number }> {
		if ((image as ITransformedCloudinaryImage).fileInfoUrl) {
			const response = await Axios.get((image as ITransformedCloudinaryImage).fileInfoUrl as string);

			return response.data.output as { width: number; height: number };
		}

		if (process.env.CONTEXT === 'fractal') {
			if (image.width && image.height) {
				return { width: image.width, height: image.height };
			}
		}

		throw new Error('Only can get output size for cloudinary image.');
	}
}
